import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { PaginatorModule, PaginatorState } from 'primeng/paginator';
import { Subject, Subscription, debounceTime } from 'rxjs';
import { AccountingDocumentGroup, AccountsService } from '../accounts.service';
import { LocaleService } from '../../i18n/locale.service';

type DirectoryMode = 'LIST' | 'CRM' | 'PARTY_TYPE';
type GroupFilters = Record<string, string | number | boolean | number[] | string[] | null | undefined>;

@Component({
    selector: 'app-accounting-directory-controls',
    standalone: true,
    imports: [CommonModule, FormsModule, PaginatorModule],
    template: `
        <div class="directory-toolbar">
            <label>
                <span class="sr-only">{{ t('pages.accountingDirectory.search') }}</span>
                <input type="search" [attr.placeholder]="t('pages.accountingDirectory.search')"
                    [(ngModel)]="searchInput" (ngModelChange)="typed.next($event)" />
            </label>
            <div class="directory-modes" role="group" [attr.aria-label]="t('pages.accountingDirectory.view')">
                <button type="button" [attr.aria-pressed]="mode === 'LIST'" (click)="selectMode('LIST')">{{ t('pages.accountingDirectory.list') }}</button>
                <button type="button" [attr.aria-pressed]="mode === 'CRM'" (click)="selectMode('CRM')">{{ t('pages.accountingDirectory.crm') }}</button>
                <button type="button" [attr.aria-pressed]="mode === 'PARTY_TYPE'" (click)="selectMode('PARTY_TYPE')">{{ t('pages.accountingDirectory.partyType') }}</button>
            </div>
            <div class="directory-modes" role="group" [attr.aria-label]="t('pages.accountingDirectory.status')">
                <button type="button" [attr.aria-pressed]="isPending" (click)="pending.emit()">{{ t('pages.accountingDirectory.pending') }}</button>
                <button type="button" [attr.aria-pressed]="!selectedStatuses.length" (click)="all.emit()">{{ t('pages.accountingDirectory.allStatuses') }}</button>
            </div>
        </div>
        <div *ngIf="mode !== 'LIST'" class="directory-groups">
            <table>
                <thead><tr>
                    <th>{{ mode === 'CRM' ? t('pages.accountingDirectory.crm') : t('pages.accountingDirectory.partyType') }}</th>
                    <th>{{ t('pages.accountingDirectory.currency') }}</th>
                    <th>{{ t('pages.accountingDirectory.pendingQty') }}</th>
                    <th>{{ t('pages.accountingDirectory.pendingAmount') }}</th>
                    <th>{{ t('pages.accountingDirectory.completedQty') }}</th>
                    <th>{{ t('pages.accountingDirectory.completedAmount') }}</th>
                </tr></thead>
                <tbody>
                    <tr *ngIf="loading"><td colspan="6">{{ t('common.loading') }}</td></tr>
                    <tr *ngFor="let row of groups">
                        <td>{{ groupLabel(row) }}</td><td>{{ row.currencyCode }}</td>
                        <td>{{ row.pendingCount }}</td><td>{{ row.pendingAmount | number:'1.2-4' }}</td>
                        <td>{{ row.completedCount }}</td><td>{{ row.completedAmount | number:'1.2-4' }}</td>
                    </tr>
                    <tr *ngIf="!loading && !groups.length"><td colspan="6">{{ error || t('pages.accountingDirectory.noGroups') }}</td></tr>
                </tbody>
            </table>
            <p-paginator [first]="first" [rows]="rows" [totalRecords]="total" (onPageChange)="pageChanged($event)"></p-paginator>
        </div>
    `,
    styles: [`
        .directory-toolbar { display:flex; flex-wrap:wrap; align-items:center; gap:.75rem; margin:0 0 1rem }
        .directory-toolbar input { min-width:16rem; max-width:100%; padding:.6rem; border:1px solid var(--surface-border,#ccc); border-radius:.5rem }
        .directory-modes { display:flex; gap:.25rem }
        .directory-modes button { padding:.55rem .75rem; border:1px solid var(--surface-border,#ccc); border-radius:.4rem; background:var(--surface-card,#fff); cursor:pointer }
        .directory-modes button[aria-pressed="true"] { background:var(--primary-color,#2563eb); color:var(--primary-color-text,#fff) }
        .directory-groups { overflow-x:auto }
        .directory-groups table { width:100%; border-collapse:collapse }
        .directory-groups th,.directory-groups td { padding:.75rem; border-bottom:1px solid var(--surface-border,#ddd); text-align:start }
        .sr-only { position:absolute; width:1px; height:1px; overflow:hidden; clip:rect(0,0,0,0) }
    `]
})
export class AccountingDirectoryControlsComponent implements OnChanges, OnDestroy {
    private readonly accountsService = inject(AccountsService);
    private readonly locale = inject(LocaleService);
    @Input({ required: true }) documentType = '';
    @Input() filters: GroupFilters = {};
    @Input() searchText = '';
    @Input() selectedStatuses: string[] = [];
    @Input() pendingStatuses: string[] = [];
    @Output() searchTextChange = new EventEmitter<string>();
    @Output() modeChange = new EventEmitter<DirectoryMode>();
    @Output() pending = new EventEmitter<void>();
    @Output() all = new EventEmitter<void>();
    readonly typed = new Subject<string>();
    private readonly typingSubscription = this.typed.pipe(debounceTime(300))
        .subscribe(value => this.searchTextChange.emit(value.trim()));
    private groupSubscription?: Subscription;
    searchInput = '';
    mode: DirectoryMode = 'LIST';
    groups: AccountingDocumentGroup[] = [];
    first = 0;
    rows = 20;
    total = 0;
    loading = false;
    error = '';

    get isPending(): boolean {
        return this.selectedStatuses.length === this.pendingStatuses.length
            && this.pendingStatuses.every(status => this.selectedStatuses.includes(status));
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['searchText']) this.searchInput = this.searchText;
        if ((changes['filters'] || changes['documentType']) && this.mode !== 'LIST') {
            this.first = 0;
            this.loadGroups();
        }
    }

    selectMode(mode: DirectoryMode): void {
        if (this.mode === mode) return;
        this.mode = mode;
        this.first = 0;
        this.modeChange.emit(mode);
        if (mode === 'LIST') this.groupSubscription?.unsubscribe();
        else this.loadGroups();
    }

    pageChanged(event: PaginatorState): void {
        this.first = event.first ?? 0;
        this.rows = event.rows ?? 20;
        this.loadGroups();
    }

    groupLabel(row: AccountingDocumentGroup): string {
        if (row.groupKey === 'UNLINKED') return this.t('pages.accountingDirectory.unlinked');
        if (row.groupKey === 'UNCLASSIFIED') return this.t('pages.accountingDirectory.unclassified');
        if (this.mode === 'PARTY_TYPE')
            return this.locale.translateIfPresent(`pages.accountingDirectory.partyKinds.${row.groupKey}`, row.groupLabel);
        return row.groupLabel;
    }

    t(key: string): string {
        this.locale.locale();
        return this.locale.translate(key);
    }

    private loadGroups(): void {
        this.groupSubscription?.unsubscribe();
        if (!this.filters['companyId'] && !this.filters['owningCompanyId'] && !this.filters['companyGroupId']) {
            this.groups = [];
            this.total = 0;
            this.loading = false;
            this.error = '';
            return;
        }
        this.loading = true;
        this.error = '';
        this.groupSubscription = this.accountsService.getDocumentGroups(this.documentType,
            this.mode as 'CRM' | 'PARTY_TYPE', this.first / this.rows, this.rows, this.filters).subscribe({
            next: result => { this.groups = result.items; this.total = result.count; this.loading = false; },
            error: () => { this.groups = []; this.total = 0; this.error = this.t('pages.accountingDirectory.loadFailed'); this.loading = false; }
        });
    }

    ngOnDestroy(): void {
        this.typingSubscription.unsubscribe();
        this.groupSubscription?.unsubscribe();
    }
}
